import { cn } from "@/lib/utils";
import {
  ALLOWED_PRODUCT_TYPE_FOR_TEXT,
  GroupedLineItem,
  LINE_ITEM_TYPES,
  // INTERNAL_LINE_ITEM_TYPES,
  LineItem,
  LineItemType,
  UI_LINE_ITEM_TYPES,
} from "@/models/line-item";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Check,
  ChevronDownIcon,
  ChevronRightIcon,
  Download,
  FileText as FileIcon,
  FolderKanban,
  GripVertical,
  Image as ImageIcon,
  Images,
  Import,
  Loader2,
  MinusIcon,
  PlusIcon,
  Trash2,
  Unlink,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import React, { useCallback, useEffect, useMemo } from "react";
import { Button } from "../base/button";
// import { Card, CardContent } from "../base/card";
import { Photo, QuoteCalculator } from "@/models";
import { MaterialFormula } from "@/models/material-formula";
import { fromApiModel as templateLineItemFromApiModel } from "@/models/quote-template-line-item";
import { Warranty } from "@/models/warranty";
import { PhotoService, QuoteTemplateService, S3Service } from "@/services";
import { MaterialService } from "../../services/material";
import { Checkbox } from "../base/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../base/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../base/dialog";
import { Input } from "../base/input";
import { Popover, PopoverContent, PopoverTrigger } from "../base/popover";
import { Progress } from "../base/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "../base/tooltip";
import { OptionWithGreatGrandChildren } from "../controls";
import { LineItemForm } from "./line-item-form";
import { useLineItemsContext } from "./quote-form";
import { CrewRates } from "@/models/crew-rates";

// Add the labor type interface
interface LaborTypeOption {
  key: string;
  name: string;
  children?: LaborTypeOption[];
}

// Interface for the component props
interface LineItemsGroupProps {
  items: LineItem[];
  onChange: (items: LineItem[]) => void;
  isTemplate?: boolean;
  workTypes?: string[];
  apiUrl?: string;
  apiStage?: string;
  warranties?: Warranty[];
  materialFormulas?: MaterialFormula[];
  quoteCalculator?: QuoteCalculator | null;
  sectionId?: number | null | undefined;
  sectionTrackingId?: string | null;
  sectionName?: string | null;
  workOrderType?: string | null;
  workOrderId?: number | null;
  jobId?: number | null;
  companyName?: string | null;
  refreshSection?: () => void;
  crewRates?: CrewRates | null;
  laborTypeOptions?: LaborTypeOption[]; // Use proper type
}
/**
 * Converts a flat array of line items to a hierarchical structure
 * Each GroupStart item begins a new group, with items nested until a GroupEnd
 * Text items will have their Product items as children
 */
function flatToHierarchical(items: LineItem[]): GroupedLineItem[] {
  // Sort items by order first to ensure correct hierarchy
  const sortedItems = [...items].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );

  const result: GroupedLineItem[] = [];
  const groupStack: GroupedLineItem[] = [];
  const textItems: Record<string, GroupedLineItem> = {}; // Track Text items by trackingId

  for (let i = 0; i < sortedItems.length; i++) {
    const item = sortedItems[i];

    // Convert item to GroupedLineItem
    const groupedItem: GroupedLineItem = {
      ...item,
      isGroup: item.type === "GroupStart",
      isText: item.type === "Text",
      children: [],
    };

    // Track Text items for later product assignment
    if (item.type === "Text") {
      textItems[item.trackingId] = groupedItem;
    }

    if (item.type === "GroupStart") {
      const groupStartId = item.id;
      let groupEndId: number | undefined;

      for (let j = i + 1; j < sortedItems.length; j++) {
        const nextItem = sortedItems[j];
        if (nextItem.type === "GroupEnd") {
          groupEndId = nextItem.id;
          break;
        }
      }

      // Create a new group
      groupedItem.groupStartId = groupStartId;
      groupedItem.groupEndId = groupEndId;

      // If inside another group, add as child
      if (groupStack.length > 0) {
        const parentGroup = groupStack[groupStack.length - 1];
        if (parentGroup.children) {
          parentGroup.children.push(groupedItem);
        }
      } else {
        // Otherwise add to root level
        result.push(groupedItem);
      }

      // This group becomes the new parent
      groupStack.push(groupedItem);
    } else if (item.type === "GroupEnd") {
      // End the current group
      if (groupStack.length > 0) {
        groupStack.pop();
      }
    } else if (ALLOWED_PRODUCT_TYPE_FOR_TEXT.includes(item.type as LineItemType) && item.parentTextTrackingId) {
      // This is a product item for a Text item
      const parentText = textItems[item.parentTextTrackingId];
      if (parentText && parentText.children) {
        parentText.children.push(groupedItem);
      }
    } else {
      // Regular item - add to current group or root
      if (groupStack.length > 0) {
        const currentGroup = groupStack[groupStack.length - 1];
        if (currentGroup.children) {
          currentGroup.children.push(groupedItem);
        }
      } else {
        result.push(groupedItem);
      }
    }
  }

  return result;
}

/**
 * Converts a hierarchical structure back to a flat array
 * Maintains proper order and ensures consistent group structure
 * Flattens Text items with their nested Product items
 */
function hierarchicalToFlat(items: GroupedLineItem[]): LineItem[] {
  const result: LineItem[] = [];
  let orderCounter = 0;

  // Recursive function to process items and their children
  const processItems = (items: GroupedLineItem[]) => {
    for (const item of items) {
      if (item.isGroup && item.children) {
        // Add the group start item
        const groupStart: LineItem = {
          id: item.groupStartId,
          trackingId: item.trackingId,
          type: "GroupStart",
          title: item.title,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unitCost: item.unitCost,
          cost: item.cost,
          markup: item.markup,
          applyTax: item.applyTax,
          excludeAdditionalMarkup: item.excludeAdditionalMarkup,
          materialTypeId: item.materialTypeId,
          materialStyleId: item.materialStyleId,
          materialSupplierId: item.materialSupplierId,
          laborCategoryId: item.laborCategoryId,
          laborTypeId: item.laborTypeId,
          typeId: item.typeId,
          useQuoteFile: item.useQuoteFile,
          useClaimFile: item.useClaimFile,
          useWorkOrderFile: item.useWorkOrderFile,
          date: item.date,
          accepted: item.accepted,
          initials: item.initials,
          reason: item.reason,
          reasonNotes: item.reasonNotes,
          order: orderCounter++,
          materialStyle: item.materialStyle,
          groupName: item.groupName,
          isCollapsed: item.isCollapsed,
          sectionId: item.sectionId,
          appliedFormulaId: item.appliedFormulaId,
          parentTextTrackingId: null,
        };

        result.push(groupStart);

        // Process children
        processItems(item.children);

        // Add the group end marker
        const groupEnd: LineItem = {
          id: item.groupEndId,
          trackingId: `${item.trackingId}-end`,
          type: "GroupEnd",
          title: null,
          description: null,
          quantity: null,
          unit: null,
          unitCost: null,
          cost: null,
          markup: null,
          applyTax: false,
          excludeAdditionalMarkup: false,
          materialTypeId: null,
          materialStyleId: null,
          materialSupplierId: null,
          laborCategoryId: null,
          laborTypeId: null,
          typeId: null,
          useQuoteFile: false,
          useClaimFile: false,
          useWorkOrderFile: false,
          date: null,
          accepted: false,
          initials: null,
          reason: null,
          reasonNotes: null,
          order: orderCounter++,
          materialStyle: null,
          groupName: item.groupName,
          isCollapsed: false,
          sectionId: item.sectionId,
          appliedFormulaId: item.appliedFormulaId,
          parentTextTrackingId: null,
        };

        result.push(groupEnd);
      } else if (item.isText) {
        // Add the Text item itself
        const textItem: LineItem = {
          ...item,
          type: "Text",
          order: orderCounter++,
          parentTextTrackingId: null,
        };
        result.push(textItem);

        // Then add all its product children
        if (item.children && item.children.length > 0) {
          for (const child of item.children) {
            const childItem: LineItem = {
              ...child,
              order: orderCounter++,
              parentTextTrackingId: item.trackingId,
            };
            result.push(childItem);
          }
        }
      } else {
        // Regular item
        const regularItem: LineItem = {
          ...item,
          order: orderCounter++,
          parentTextTrackingId: item.parentTextTrackingId || null,
        };

        result.push(regularItem);
      }
    }
  };

  processItems(items);
  return result;
}

export function EmptyGroupPlaceholder({ groupId }: { groupId: string }) {
  // Make this a droppable target using dnd-kit
  const placeholderId = `empty-placeholder-${groupId}`;
  const { setNodeRef, isOver } = useSortable({
    id: placeholderId,
    data: {
      type: "empty-placeholder",
      groupId,
    },
  });

  return (
    <motion.div
      ref={setNodeRef}
      // initial={{ opacity: 0 }}
      // animate={{ opacity: 1 }}
      // exit={{ opacity: 0 }}
      className={`flex items-center justify-center p-4 py-8 my-2 border-2 border-dashed rounded-md ${
        isOver
          ? "border-blue-500 bg-blue-100/50"
          : "border-blue-200 bg-blue-50/50"
      }`}
      key={placeholderId}
      id={placeholderId}
      data-id={placeholderId}
    >
      <div
        className={`py-2 text-sm text-center ${
          isOver ? "text-blue-600" : "text-blue-500"
        }`}
      >
        Drag items here
      </div>
    </motion.div>
  );
}

function EmptyTextProductsPlaceholder({ textId }: { textId: string }) {
  // Make this a droppable target for Text products using dnd-kit
  const placeholderId = `empty-text-products-${textId}`;
  const { setNodeRef, isOver } = useSortable({
    id: placeholderId,
    data: {
      type: "empty-text-products",
      textId,
    },
  });

  return (
    <motion.div
      ref={setNodeRef}
      className={`flex items-center justify-center p-3 py-6 my-2 border-2 border-dashed rounded-md ${
        isOver
          ? `border-${
              LINE_ITEM_TYPES.find((t) => t.value === "Text")?.color
            } bg-${LINE_ITEM_TYPES.find((t) => t.value === "Text")?.color}/10`
          : `border-${
              LINE_ITEM_TYPES.find((t) => t.value === "Text")?.color
            }/50 bg-${
              LINE_ITEM_TYPES.find((t) => t.value === "Text")?.color
            }/5`
      }`}
      key={placeholderId}
      id={placeholderId}
      data-id={placeholderId}
    >
      <div
        className={`py-2 text-sm text-center ${
          isOver
            ? `text-${LINE_ITEM_TYPES.find((t) => t.value === "Text")?.color}`
            : `text-${
                LINE_ITEM_TYPES.find((t) => t.value === "Text")?.color
              }/50`
        }`}
      >
        Drag products or labor items here
      </div>
    </motion.div>
  );
}

export { EmptyTextProductsPlaceholder };

// Create a new SortableGroupHeader component
export function SortableGroupHeader({
  item,
  toggleGroupCollapse,
  handleGroupNameChange,
  handleRemove,
  isPreview = false,
  children,
  groupIndex,
}: {
  item: GroupedLineItem;
  toggleGroupCollapse: (id: string) => void;
  handleGroupNameChange: (id: string, name: string) => void;
  handleRemove: (id: string) => void;
  isPreview?: boolean;
  children: React.ReactNode;
  groupIndex?: number;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    isOver,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.trackingId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      style={style}
      className="group-header"
      data-group-header="true"
      data-group-id={item.trackingId}
      data-group-index={groupIndex ?? -1}
    >
      <div
        ref={setNodeRef}
        className={cn(
          "flex items-center h-12 p-2 mb-2 border rounded bg-background transition-colors duration-200 overflow-visible",
          isOver && "border-blue-500 bg-blue-50"
        )}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="items-center justify-center w-6 h-6 p-1 mr-2"
          onClick={() => toggleGroupCollapse(item.trackingId)}
        >
          {item.isCollapsed ? (
            <ChevronRightIcon className="w-4 h-4" />
          ) : (
            <ChevronDownIcon className="w-4 h-4" />
          )}
        </Button>

        <div className="flex items-center gap-2">
          <button
            {...(isPreview ? {} : attributes)}
            {...(isPreview ? {} : listeners)}
            className="cursor-move touch-none"
            type="button"
          >
            <GripVertical className="w-4 h-4" />
          </button>

          <div className="relative flex">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative flex items-center gap-2 p-2 border rounded-md">
                    <FolderKanban className="w-4 h-4 text-blue-500" />
                  </div>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent>
                    <p>Group</p>
                    <p className="text-xs text-muted-foreground">
                      A collection of related line items
                    </p>
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </TooltipProvider>
          </div>

          {!isPreview && (
            <div className="relative flex">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon-sm"
                        >
                          <MinusIcon className="w-4 h-4" />
                          <span className="sr-only">Remove group</span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80">
                        <div className="space-y-4">
                          <p className="text-sm">
                            Are you sure you want to remove this group?
                          </p>
                          <div className="flex justify-end gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={(e) => {
                                e.preventDefault();
                                const popover =
                                  e.currentTarget.closest('[role="dialog"]');
                                if (popover) {
                                  const trigger = document.querySelector(
                                    `[aria-controls="${popover.id}"]`
                                  );
                                  trigger?.dispatchEvent(
                                    new Event("click", { bubbles: true })
                                  );
                                }
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              variant="destructive"
                              onClick={() => handleRemove(item.trackingId)}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </TooltipTrigger>
                  <TooltipPortal>
                    <TooltipContent>
                      <p>Remove</p>
                    </TooltipContent>
                  </TooltipPortal>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </div>

        <Input
          value={item.groupName || ""}
          onChange={(e) =>
            handleGroupNameChange(item.trackingId, e.target.value)
          }
          className="flex-grow ml-2 text-sm font-medium h-7"
          placeholder="Group Name"
          readOnly={isPreview}
        />
      </div>
      {children}
    </div>
  );
}

// Update FileExplorer component with presigned URL support
function FileExplorer({
  items,
  onRemove,
  s3Service,
  companyName,
  jobId,
  photos = [],
  sectionId,
  photoService,
  isLoadingRemove,
  fetchPhotos,
}: {
  items: LineItem[];
  onRemove: (id: string, isPhoto: boolean) => void;
  s3Service: S3Service;
  companyName?: string | null;
  jobId?: number | null;
  photos?: Photo[];
  sectionId?: number | null;
  photoService: PhotoService;
  isLoadingRemove: Record<string, boolean>;
  fetchPhotos: () => void;
}) {
  // Combine file items and photos
  const fileItems = items.filter(
    (item) => item.type === "File" || item.type === "Photo"
  );

  // Create LineItem-like objects from photos for display
  const photoItems = React.useMemo(() => {
    return photos.map((photo) => ({
      id: photo.photoid,
      trackingId: `photo-${photo.photoid}`,
      type: "Photo" as LineItemType,
      title: photo.photoname || "Photo",
      filenameS3: `${photo.photoname_s3}.${photo.photoext}`,
      description: photo.photolabel || null,
      quantity: null,
      unit: null,
      unitCost: null,
      cost: null,
      markup: null,
      applyTax: false,
      excludeAdditionalMarkup: false,
      materialTypeId: null,
      materialStyleId: null,
      materialSupplierId: null,
      laborCategoryId: null,
      laborTypeId: null,
      typeId: null,
      useQuoteFile: false,
      useClaimFile: false,
      useWorkOrderFile: false,
      date: photo.photodts?.toISOString() || null,
      accepted: false,
      initials: null,
      reason: null,
      reasonNotes: null,
      order: null,
      materialStyle: null,
      groupName: null,
      isCollapsed: false,
      sectionId: sectionId,
      appliedFormulaId: null,
    }));
  }, [photos, sectionId]);

  // Combine both types of items
  const allItems = React.useMemo(
    () => [...fileItems, ...photoItems],
    [fileItems, photoItems]
  );

  const [presignedUrls, setPresignedUrls] = React.useState<
    Record<string, string>
  >({});
  const [isLoadingDownload, setIsLoadingDownload] = React.useState<
    Record<string, boolean>
  >({});
  const [isLoadingUnlink, setIsLoadingUnlink] = React.useState<
    Record<string, boolean>
  >({});

  // Handle unlinking a photo
  const handleUnlinkPhoto = async (trackingId: string) => {
    if (!trackingId.startsWith("photo-")) return;

    try {
      setIsLoadingUnlink((prev) => ({ ...prev, [trackingId]: true }));

      const photoId = parseInt(trackingId.split("-")[1]);

      // Update the photo to remove section tracking ID
      await photoService.updatePhotos([
        {
          photoid: photoId,
          psectiontrackingid: null,
        },
      ]);
    } catch (error) {
      console.error("Error unlinking photo:", error);
    } finally {
      fetchPhotos();
      setIsLoadingUnlink((prev) => ({ ...prev, [trackingId]: false }));
    }
  };

  // Load thumbnails for photos
  React.useEffect(() => {
    const loadThumbnails = async () => {
      const photoItems = allItems.filter(
        (item) => item.type === "Photo" && item.filenameS3
      );

      for (const item of photoItems) {
        if (!item.filenameS3 || presignedUrls[item.trackingId]) continue;

        try {
          const key = item.filenameS3;
          const response = await s3Service.getPresignedUrl(
            `${companyName}/job/${jobId}${
              item.type === "File" ? "/files" : ""
            }/${key}`
          );
          const presignedUrl = response.presignedURLs[1]?.getURL;

          if (presignedUrl) {
            setPresignedUrls((prev) => ({
              ...prev,
              [item.trackingId]: presignedUrl,
            }));
          }
        } catch (error) {
          console.error("Error getting presigned URL for thumbnail:", error);
        }
      }
    };

    void loadThumbnails();
  }, [allItems, s3Service, presignedUrls, companyName, jobId]);

  if (allItems.length === 0) return null;

  const handleDownload = async (item: LineItem) => {
    if (!item.filenameS3) return;

    try {
      setIsLoadingDownload((prev) => ({ ...prev, [item.trackingId]: true }));
      const key = item.filenameS3;
      const response = await s3Service.getPresignedUrl(
        `${companyName}/job/${jobId}${
          item.type === "File" ? "/files" : ""
        }/${key}`
      );
      const presignedUrl = response.presignedURLs[0]?.getURL;

      if (presignedUrl) {
        // Create a temporary link and click it to trigger download
        const link = document.createElement("a");
        link.href = presignedUrl;
        link.download = item.title || item.filenameS3 || "download";
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Error getting presigned URL:", error);
    } finally {
      setIsLoadingDownload((prev) => ({ ...prev, [item.trackingId]: false }));
    }
  };

  return (
    <div className="mt-4">
      <h3 className="mb-4 text-sm font-medium">Uploaded Files & Photos</h3>
      <div className="flex flex-wrap gap-4">
        {allItems.map((item) => (
          <div
            key={item.trackingId}
            className="relative w-20 p-1 transition-colors rounded-lg group hover:bg-primary/5"
          >
            <div className="flex flex-col items-center gap-2 text-center">
              {item.type === "Photo" ? (
                presignedUrls[item.trackingId] ? (
                  <div className="relative w-16 h-16">
                    <img
                      src={presignedUrls[item.trackingId]}
                      alt={item.title || ""}
                      className="object-cover w-full h-full rounded"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-16 h-16">
                    <ImageIcon className="w-8 h-8 text-blue-500" />
                  </div>
                )
              ) : (
                <div className="flex items-center justify-center w-16 h-16">
                  <FileIcon className="w-8 h-8 text-gray-500" />
                </div>
              )}
              <span
                className="w-full text-sm font-medium truncate"
                title={item.title || ""}
              >
                {item.title}
              </span>
              {/* <span className="text-xs text-muted-foreground">
                {formatFileSize(item.size || 0)}
              </span> */}
            </div>
            <div className="absolute transition-opacity opacity-0 -top-2 -right-2 group-hover:opacity-100">
              <div className="flex flex-col gap-2">
                <Button
                  title="Remove file"
                  type="button"
                  variant="destructive"
                  size="icon"
                  onClick={() =>
                    onRemove(item.trackingId, item.type === "Photo")
                  }
                  disabled={isLoadingRemove[item.trackingId]}
                >
                  {isLoadingRemove[item.trackingId] ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3" />
                  )}
                </Button>
                <Button
                  title="Download file"
                  type="button"
                  variant="default"
                  size="icon"
                  onClick={() => handleDownload(item)}
                  disabled={isLoadingDownload[item.trackingId]}
                >
                  {isLoadingDownload[item.trackingId] ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Download className="w-3 h-3" />
                  )}
                </Button>
                {item.type === "Photo" && (
                  <Button
                    title="Unlink from section"
                    type="button"
                    variant="secondary"
                    size="icon"
                    onClick={() => handleUnlinkPhoto(item.trackingId)}
                    disabled={isLoadingUnlink[item.trackingId]}
                  >
                    {isLoadingUnlink[item.trackingId] ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Unlink className="w-3 h-3" />
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Add helper function for file size formatting
// function formatFileSize(bytes: number): string {
//   if (bytes === 0) return '0 B';
//   const k = 1024;
//   const sizes = ['B', 'KB', 'MB', 'GB'];
//   const i = Math.floor(Math.log(bytes) / Math.log(k));
//   return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
// }

// PhotoPickerModal Component
function PhotoPickerModal({
  open,
  onOpenChange,
  jobId,
  sectionTrackingId,
  companyName,
  photoService,
  s3Service,
  excludePhotos,
  onPhotosSelected,
  sectionName,
  refreshSection,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: number | null;
  sectionTrackingId: string | null | undefined;
  companyName: string | null | undefined;
  photoService: PhotoService;
  s3Service: S3Service;
  excludePhotos: number[];
  onPhotosSelected: (photos: Photo[]) => void;
  sectionName?: string | null;
  refreshSection?: () => void;
}) {
  const [jobPhotos, setJobPhotos] = React.useState<Photo[]>([]);
  const [selectedPhotos, setSelectedPhotos] = React.useState<Photo[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [presignedUrls, setPresignedUrls] = React.useState<
    Record<number, string>
  >({});
  const [loadingPhotos, setLoadingPhotos] = React.useState<Set<number>>(
    new Set()
  );

  // Load a thumbnail for a photo
  const loadThumbnail = useCallback(
    async (photo: Photo) => {
      if (!photo.photoname_s3 || !companyName || !jobId) return;

      try {
        const key = `${photo.photoname_s3}.${photo.photoext}`;
        const response = await s3Service.getPresignedUrl(
          `${companyName}/job/${jobId}/${key}`
        );
        const presignedUrl = response.presignedURLs[1]?.getURL;

        if (presignedUrl) {
          setPresignedUrls((prev) => ({
            ...prev,
            [photo.photoid]: presignedUrl,
          }));
        }
      } catch (error) {
        console.error("Error getting presigned URL for thumbnail:", error);
      } finally {
        setLoadingPhotos((prev) => {
          const next = new Set(prev);
          next.delete(photo.photoid);
          return next;
        });
      }
    },
    [s3Service, companyName, jobId]
  );

  // Fetch photos from job that aren't already in this section
  React.useEffect(() => {
    const fetchJobPhotos = async () => {
      if (!jobId || !open) return;

      setIsLoadingInitial(true);
      try {
        // Exclude photos that are already in this section
        const photos = await photoService.getPhotosByJobId(
          jobId,
          excludePhotos
        );
        setJobPhotos(photos);

        // Start loading thumbnails for the first batch
        const initialBatch = photos.slice(0, 20);
        setLoadingPhotos(new Set(initialBatch.map((p) => p.photoid)));

        // Load thumbnails in batches
        for (const photo of initialBatch) {
          await loadThumbnail(photo);
        }
      } catch (error) {
        console.error("Failed to fetch job photos:", error);
      } finally {
        setIsLoadingInitial(false);
      }
    };

    void fetchJobPhotos();
  }, [
    jobId,
    open,
    photoService,
    sectionTrackingId,
    excludePhotos,
    s3Service,
    companyName,
    loadThumbnail,
  ]);

  // Handle lazy loading more thumbnails as user scrolls
  const photoGridRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const photoId = parseInt(
              entry.target.getAttribute("data-photo-id") || "0"
            );
            const photo = jobPhotos.find((p) => p.photoid === photoId);

            if (
              photo &&
              !presignedUrls[photoId] &&
              !loadingPhotos.has(photoId)
            ) {
              setLoadingPhotos((prev) => {
                const next = new Set(prev);
                next.add(photoId);
                return next;
              });

              void loadThumbnail(photo);
            }
          }
        });
      },
      { rootMargin: "100px" }
    );

    const photoElements = document.querySelectorAll("[data-photo-id]");
    photoElements.forEach((el) => observer.observe(el));

    return () => {
      photoElements.forEach((el) => observer.unobserve(el));
      observer.disconnect();
    };
  }, [
    jobPhotos,
    presignedUrls,
    loadingPhotos,
    s3Service,
    companyName,
    jobId,
    loadThumbnail,
  ]);

  // Toggle photo selection
  const togglePhotoSelection = (photo: Photo) => {
    setSelectedPhotos((prev) => {
      const isSelected = prev.some((p) => p.photoid === photo.photoid);
      if (isSelected) {
        return prev.filter((p) => p.photoid !== photo.photoid);
      } else {
        return [...prev, photo];
      }
    });
  };

  // Select all photos
  const selectAll = () => {
    setSelectedPhotos(jobPhotos);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedPhotos([]);
  };

  const handleAddSelectedPhotos = async () => {
    if (selectedPhotos.length === 0 || !sectionTrackingId) return;

    setIsSubmitting(true);
    try {
      // Update the section tracking ID for all selected photos
      const photosToUpdate = selectedPhotos.map((photo) => ({
        photoid: photo.photoid,
        psectiontrackingid: sectionTrackingId,
      }));

      await photoService.updatePhotos(photosToUpdate);

      // Return the updated photos for addition to the section
      const updatedPhotos = selectedPhotos.map((photo) => ({
        ...photo,
        psectiontrackingid: sectionTrackingId,
      }));

      onPhotosSelected(updatedPhotos);
      onOpenChange(false);
      refreshSection?.();
    } catch (error) {
      console.error("Failed to update photos:", error);
    } finally {
      clearSelection();
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Select Photos from Job</DialogTitle>
          <DialogDescription>
            Choose photos to add to {sectionName ? <span className="font-bold">{sectionName}</span> : "this section"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between my-2">
          <div className="space-x-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={selectAll}
              disabled={isLoadingInitial || isSubmitting || jobPhotos.length === 0}
            >
              Select All
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearSelection}
              disabled={isLoadingInitial || isSubmitting || selectedPhotos.length === 0}
            >
              Clear Selection
            </Button>
          </div>
          <div className="text-sm text-muted-foreground">
            {selectedPhotos.length} of {jobPhotos.length} selected
          </div>
        </div>

        {isLoadingInitial && jobPhotos.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : jobPhotos.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No photos found in this job that aren't already in this section.
          </div>
        ) : (
          <div
            ref={photoGridRef}
            className="grid grid-cols-2 gap-4 p-2 overflow-y-auto sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
            style={{ maxHeight: "calc(80vh - 250px)" }}
          >
            {jobPhotos.map((photo) => (
              <div
                key={photo.photoid}
                className={`relative group border rounded-md overflow-hidden cursor-pointer transition-all ${
                  selectedPhotos.some((p) => p.photoid === photo.photoid)
                    ? "ring-2 ring-primary border-primary"
                    : "hover:border-muted-foreground"
                }`}
                onClick={() => togglePhotoSelection(photo)}
                data-photo-id={photo.photoid}
              >
                <div className="flex items-center justify-center aspect-square bg-slate-100">
                  {presignedUrls[photo.photoid] ? (
                    <img
                      src={presignedUrls[photo.photoid]}
                      alt={photo.photoname || "Photo"}
                      className="object-cover w-full h-full"
                    />
                  ) : loadingPhotos.has(photo.photoid) ? (
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-muted-foreground" />
                  )}
                </div>

                <div className="absolute top-2 left-2">
                  <Checkbox
                    checked={selectedPhotos.some(
                      (p) => p.photoid === photo.photoid
                    )}
                    onCheckedChange={() => togglePhotoSelection(photo)}
                    className="h-5 w-5 border-2 border-white bg-white/50 backdrop-blur-sm data-[state=checked]:bg-primary data-[state=checked]:text-white pointer-events-none"
                  />
                </div>

                {photo.photolabel && (
                  <div className="absolute bottom-0 left-0 right-0 p-1 text-xs text-white truncate bg-black/50">
                    {photo.photolabel}
                  </div>
                )}

                {selectedPhotos.some((p) => p.photoid === photo.photoid) && (
                  <div className="absolute inset-0 flex items-center justify-center bg-primary/20">
                    <Check className="w-8 h-8 text-primary stroke-[3]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleAddSelectedPhotos}
            disabled={isLoadingInitial || isSubmitting || selectedPhotos.length === 0}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              `Add ${selectedPhotos.length} Photo${
                selectedPhotos.length === 1 ? "" : "s"
              }`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LineItemsGroup({
  items,
  onChange,
  isTemplate,
  workTypes = [],
  apiUrl = "",
  apiStage = "",
  warranties = [],
  materialFormulas = [],
  quoteCalculator = null,
  sectionId = null,
  sectionTrackingId = null,
  sectionName = null,
  workOrderType = null,
  workOrderId = null,
  jobId,
  companyName,
  refreshSection,
  crewRates = null,
  laborTypeOptions = [
    {
      "key": "extras",
      "name": "Extras",
      "children": [
        {
          "key": "chimney_flashings_large_1",
          "name": "Chimney Flashings Large"
        },
        {
          "key": "chimney_flashings_small_1", 
          "name": "Chimney Flashings Small"
        },
        {
          "key": "cut_in_ridge_vent_1",
          "name": "Cut In Ridge Vent"
        },
        {
          "key": "cut_in_vents_1",
          "name": "Cut In Vents"
        },
        {
          "key": "drip_edge_1",
          "name": "Drip Edge"
        },
        {
          "key": "gutter_apron_1",
          "name": "Gutter Apron"
        },
        {
          "key": "install_ridge_vent_1",
          "name": "Install Ridge Vent"
        },
        {
          "key": "plug_vents_1",
          "name": "Plug Vents"
        },
        {
          "key": "power_vents_1", 
          "name": "Power Vents"
        },
        {
          "key": "skylights_large_install_1",
          "name": "Skylights Large Install"
        },
        {
          "key": "skylights_small_install_1",
          "name": "Skylights Small Install"
        },
        {
          "key": "step_flashing_1",
          "name": "Step Flashing"
        },
        {
          "key": "straight_flashing_1",
          "name": "Straight Flashing"
        },
        {
          "key": "z_flashing_large_1",
          "name": "Z Flashing Large"
        },
        {
          "key": "z_flashing_small_1",
          "name": "Z Flashing Small"
        }
      ]
    },
    {
      "key": "install_rates",
      "name": "Install Rates",
      "children": [
        {
          "key": "4_12_1",
          "name": "4/12 Pitch"
        },
        {
          "key": "5_12_1",
          "name": "5/12 Pitch"
        },
        {
          "key": "6_12_1", 
          "name": "6/12 Pitch"
        },
        {
          "key": "7_12_1",
          "name": "7/12 Pitch"
        },
        {
          "key": "8_12_1",
          "name": "8/12 Pitch"
        },
        {
          "key": "9_12_1",
          "name": "9/12 Pitch"
        },
        {
          "key": "10_12_1",
          "name": "10/12 Pitch"
        },
        {
          "key": "11_12_1",
          "name": "11/12 Pitch"
        },
        {
          "key": "12_12_1",
          "name": "12/12 Pitch"
        },
        {
          "key": "height_upcharge_1",
          "name": "Height Upcharge"
        },
        {
          "key": "ridge_1",
          "name": "Ridge"
        },
        {
          "key": "starter_1", 
          "name": "Starter"
        },
        {
          "key": "walk_on_1",
          "name": "Walk On"
        }
      ]
    },
    {
      "key": "tearoff",
      "name": "Tearoff",
      "children": [
        {
          "key": "additional_layer_upcharge_1",
          "name": "Additional Layer Upcharge"
        },
        {
          "key": "plywood_replacement_1",
          "name": "Plywood Replacement"
        },
        {
          "key": "tearoff_1",
          "name": "Tearoff"
        }
      ]
    },
    {
      "key": "hourly",
      "name": "Hourly",
      "children": [
        {
          "key": "hourly_1",
          "name": "Hourly Installer"
        }
      ]
    }
  ]  // Add default value
}: Readonly<LineItemsGroupProps>): React.ReactElement {
  // Use the shared context for section refresh notifications
  const { updatedSectionIds } = useLineItemsContext();
  
  const materialService = React.useMemo(
    () => new MaterialService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );

  const photoService = useMemo(
    () => new PhotoService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );

  const quoteTemplateService = React.useMemo(
    () => new QuoteTemplateService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );

  const [templates, setTemplates] = React.useState<
    Array<{ ntid: number; nttitle: string }>
  >([]);
  const [importingTemplateId, setImportingTemplateId] = React.useState<
    number | null
  >(null);
  const [isTemplateMenuOpen, setIsTemplateMenuOpen] = React.useState(false);
  const [isLineItemOpen, setIsLineItemOpen] = React.useState(false);
  const [photos, setPhotos] = React.useState<Photo[]>([]);
  const [isPhotoPickerOpen, setIsPhotoPickerOpen] = React.useState(false);

  const fetchTemplates = React.useCallback(async () => {
    try {
      const templates = await quoteTemplateService.getAllQuoteTemplates(
        workTypes.join(":")
      );
      setTemplates(
        templates.map((t) => ({ ntid: t.ntid!, nttitle: t.nttitle ?? "" }))
      );
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  }, [quoteTemplateService, workTypes]);

  React.useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const fetchPhotos = React.useCallback(async () => {
    try {
      if (sectionTrackingId) {
        const response = await photoService.getPhotosBySectionTrackingId(
          sectionTrackingId
        );
        setPhotos(response);
      }
    } catch (error) {
      console.error("Failed to fetch photos:", error);
    }
  }, [photoService, sectionTrackingId]);

  // Add useEffect to fetch photos by section tracking ID
  React.useEffect(() => {
    void fetchPhotos();
  }, [fetchPhotos]);

  // Fetch photos when triggered by context updates
  useEffect(() => {
    if (sectionId && updatedSectionIds.includes(sectionId)) {
      fetchPhotos();
    }
  }, [updatedSectionIds, sectionId, fetchPhotos]);

  const [localProductStyleOptions, setLocalProductStyleOptions] =
    React.useState<OptionWithGreatGrandChildren[]>([]);

  const fetchMaterials = React.useCallback(
    async (workTypes: string[]) => {
      try {
        const productTypes = await materialService.getMaterialsByWorkTypes(
          workTypes
        );
        setLocalProductStyleOptions(
          () =>
            productTypes?.map((material) => ({
              value: `${material.id}`,
              label: material.name,
              children: material.brands.map((brand) => ({
                value: `${brand.id}`,
                label: brand.name,
                children: brand.styles.map((style) => ({
                  value: `${style.id}`,
                  label: style.name,
                  coverage: style.coverage || 0,
                  coverageUnit: style.coverageUnit || null,
                  children: style.suppliers.map((supplier) => ({
                    value: `${supplier.id}`,
                    label: supplier.name,
                    price: supplier.price,
                    unit: supplier.unit,
                  })),
                })),
              })),
            })) || []
        );
      } catch (err) {
        console.error(err);
      }
    },
    [materialService]
  );

  React.useEffect(() => {
    const loadMaterials = async () => {
      await fetchMaterials(workTypes);
    };
    void loadMaterials();
  }, [fetchMaterials, workTypes]);

  // Store collapsed state in a separate object for better performance
  const [collapsedGroups, setCollapsedGroups] = React.useState<
    Record<string, boolean>
  >({});

  const [isLoadingRemove, setIsLoadingRemove] = React.useState<
    Record<string, boolean>
  >({});

  // State for hierarchical data structure
  const [hierarchicalItems, setHierarchicalItems] = React.useState<
    GroupedLineItem[]
  >(() => {
    const hierarchical = flatToHierarchical(items);

    // Try to load saved collapsed states
    try {
      const groupTrackingIds = items
        .filter((item) => item.type === "GroupStart" && item.trackingId)
        .map((item) => item.trackingId)
        .filter(Boolean) as string[];

      if (groupTrackingIds.length > 0) {
        const storageKey = `lineItemGroups-${groupTrackingIds.join(
          "-"
        )}-collapsed`;
        const storedState = localStorage.getItem(storageKey);

        if (storedState) {
          const savedState = JSON.parse(storedState);
          setCollapsedGroups(savedState);

          // Apply saved state to the items
          const applyCollapsedState = (items: GroupedLineItem[]) => {
            for (const item of items) {
              if (item.isGroup) {
                item.isCollapsed = savedState[item.trackingId] || false;
              }
              if (item.children && Array.isArray(item.children)) {
                applyCollapsedState(item.children as GroupedLineItem[]);
              }
            }
          };

          applyCollapsedState(hierarchical);
        }
      }
    } catch (error) {
      console.error("Failed to load collapsed state:", error);
    }

    return hierarchical;
  });

  // State for drag & drop and popover
  const [activeId, setActiveId] = React.useState<UniqueIdentifier | null>(null);

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Generate flat list of all IDs for drag and drop
  const itemTrackingIds: string[] = useMemo(() => {
    const trackingIds: string[] = [];

    const collectIds = (items: GroupedLineItem[]) => {
      for (const item of items) {
        trackingIds.push(item.trackingId);

        // Add placeholder for empty groups
        if (
          item.isGroup &&
          item.children &&
          item.children.length === 0 &&
          !item.isCollapsed
        ) {
          trackingIds.push(`empty-placeholder-${item.trackingId}`);
        }

        // Add placeholder for empty text products area
        if (
          item.isText &&
          item.children &&
          item.children.length === 0 &&
          !item.isCollapsed
        ) {
          trackingIds.push(`empty-text-products-${item.trackingId}`);
        }

        // Add children IDs if not collapsed
        if (
          item.isGroup &&
          item.children &&
          Array.isArray(item.children) &&
          !item.isCollapsed
        ) {
          collectIds(item.children as GroupedLineItem[]);
        }

        // Add product IDs from text items if not collapsed
        // This allows dragging products directly to/from text items
        if (
          item.isText &&
          item.children &&
          Array.isArray(item.children) &&
          !item.isCollapsed
        ) {
          // Add IDs of all products in this text item
          item.children.forEach((product) => {
            trackingIds.push(product.trackingId);
          });
        }
      }
    };

    collectIds(hierarchicalItems);
    return Array.from(new Set(trackingIds)); // Remove any duplicates
  }, [hierarchicalItems]);

  React.useEffect(() => {
    const newHierarchical = flatToHierarchical(items);

    const applyCollapsedState = (items: GroupedLineItem[]) => {
      for (const item of items) {
        if (item.isGroup) {
          item.isCollapsed = collapsedGroups[item.trackingId] || false;
        }
        if (item.children && Array.isArray(item.children)) {
          applyCollapsedState(item.children as GroupedLineItem[]);
        }
      }
    };

    applyCollapsedState(newHierarchical);
    setHierarchicalItems(newHierarchical);
  }, [items, collapsedGroups]);

  /**
   * Finds an item in the hierarchical structure by ID
   * Returns the item or null if not found
   */
  const findItemByTrackingId = (
    items: GroupedLineItem[],
    id: string
  ): GroupedLineItem | null => {
    for (const item of items) {
      if (item.trackingId === id) {
        return item;
      }

      if (item.isGroup && item.children && Array.isArray(item.children)) {
        const found = findItemByTrackingId(
          item.children as GroupedLineItem[],
          id
        );
        if (found) return found;
      }
    }

    return null;
  };

  /**
   * Finds an item and its location in the hierarchical structure
   * Returns the path, parent array, and index
   */
  const findItemPath = (
    items: GroupedLineItem[],
    trackingId: string,
    path: number[] = []
  ): { path: number[]; parent: GroupedLineItem[]; index: number } | null => {
    // Handle empty placeholder IDs for groups
    if (trackingId.startsWith("empty-placeholder-")) {
      const groupTrackingId = trackingId.replace("empty-placeholder-", "");

      for (let i = 0; i < items.length; i++) {
        if (items[i].trackingId === groupTrackingId) {
          if (!items[i].children) {
            items[i].children = [];
          }
          const children = items[i].children as GroupedLineItem[];
          return {
            path: [...path, i],
            parent: children,
            index: 0,
          };
        }

        if (
          items[i].isGroup &&
          items[i].children &&
          Array.isArray(items[i].children) &&
          !items[i].isCollapsed
        ) {
          const result = findItemPath(
            items[i].children as GroupedLineItem[],
            trackingId,
            [...path, i]
          );
          if (result) return result;
        }
      }

      return null;
    }

    // Handle empty text products placeholder IDs
    if (trackingId.startsWith("empty-text-products-")) {
      const textTrackingId = trackingId.replace("empty-text-products-", "");

      for (let i = 0; i < items.length; i++) {
        if (items[i].trackingId === textTrackingId && items[i].isText) {
          if (!items[i].children) {
            items[i].children = [];
          }
          const children = items[i].children as GroupedLineItem[];
          return {
            path: [...path, i],
            parent: children,
            index: 0,
          };
        }

        // Check in groups for text items
        if (
          items[i].isGroup &&
          items[i].children &&
          Array.isArray(items[i].children) &&
          !items[i].isCollapsed
        ) {
          const result = findItemPath(
            items[i].children as GroupedLineItem[],
            trackingId,
            [...path, i]
          );
          if (result) return result;
        }
      }

      return null;
    }

    // Find regular items or product items within text
    for (let i = 0; i < items.length; i++) {
      if (items[i].trackingId === trackingId) {
        return {
          path: [...path, i],
          parent: items,
          index: i,
        };
      }

      // Check children of groups (if expanded)
      if (
        items[i].isGroup &&
        items[i].children &&
        Array.isArray(items[i].children) &&
        !items[i].isCollapsed
      ) {
        const result = findItemPath(
          items[i].children as GroupedLineItem[],
          trackingId,
          [...path, i]
        );
        if (result) return result;
      }

      // Check product items in text items (if expanded)
      if (
        items[i].isText &&
        items[i].children &&
        Array.isArray(items[i].children) &&
        !items[i].isCollapsed
      ) {
        const textChildren = items[i].children as GroupedLineItem[];
        for (let j = 0; j < textChildren.length; j++) {
          if (textChildren[j].trackingId === trackingId) {
            return {
              path: [...path, i, j],
              parent: textChildren,
              index: j,
            };
          }
        }
      }
    }

    return null;
  };

  const notifyChanges = React.useCallback(
    (items: GroupedLineItem[]) => {
      const flatItems = hierarchicalToFlat(items);
      onChange(flatItems);
    },
    [onChange]
  );

  // Handle start of drag operation
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  // Handle drag over - can be used for hover effects
  const handleDragOver = () => {
    // Future enhancement: highlight potential drop targets
  };

  // Handle end of drag operation
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    // If no change or no target, do nothing
    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    try {
      // Make a deep copy to avoid mutation issues
      const newItems = JSON.parse(
        JSON.stringify(hierarchicalItems)
      ) as GroupedLineItem[];

      // Find source and target locations
      const sourceResult = findItemPath(newItems, active.id as string);
      const targetResult = findItemPath(newItems, over.id as string);

      if (!sourceResult || !targetResult) {
        setActiveId(null);
        return;
      }

      // Extract source item and target location details
      const { parent: sourceParent, index: sourceIndex } = sourceResult;
      const sourceItem = sourceParent[sourceIndex];

      // Early check: Prevent text items from being dropped into themselves
      // This needs to happen before we start modifying target locations
      const overIdStr = over.id.toString();
      if (sourceItem.isText || sourceItem.type === "Text") {
        // Check if dropping onto the text item itself
        if (overIdStr === sourceItem.trackingId) {
          console.log("Cannot drop Text item into itself (direct)");
          setActiveId(null);
          return;
        }
        
        // Check if dropping into its empty products placeholder
        if (overIdStr === `empty-text-products-${sourceItem.trackingId}`) {
          console.log("Cannot drop Text item into its own products area");
          setActiveId(null);
          return;
        }
        
        // Check if dropping onto any of its children (products)
        if (sourceItem.children && Array.isArray(sourceItem.children)) {
          const isDroppingOnOwnChild = sourceItem.children.some(
            (child) => child.trackingId === overIdStr
          );
          if (isDroppingOnOwnChild) {
            console.log("Cannot drop Text item onto its own child");
            setActiveId(null);
            return;
          }
        }
      }

      let { parent: targetParent, index: targetIndex } = targetResult;

      // Determine if the target is a text product area
      let isTargetTextProductArea = false;
      let targetTextItem: GroupedLineItem | null = null;

      // Check if we're dropping into an empty text products placeholder
      if (overIdStr.startsWith("empty-text-products-")) {
        const textId = overIdStr.replace("empty-text-products-", "");
        targetTextItem = findItemByTrackingId(newItems, textId);
        if (targetTextItem?.isText) {
          isTargetTextProductArea = true;
          targetParent = targetTextItem.children as GroupedLineItem[];
          targetIndex = 0;
        }
      }
      // Check if the target is a text item or one of its products
      else {
        const overItem = findItemByTrackingId(newItems, overIdStr);
        if (overItem?.isText && !overItem.isCollapsed) {
          // Dropping directly onto an expanded Text item
          targetTextItem = overItem;
          isTargetTextProductArea = true;
          targetParent = overItem.children as GroupedLineItem[];
          targetIndex = 0;
        } else if (overItem?.parentTextTrackingId) {
          // Dropping onto/near an existing product of a Text item
          targetTextItem = findItemByTrackingId(
            newItems,
            overItem.parentTextTrackingId
          );
          if (targetTextItem?.isText) {
            isTargetTextProductArea = true;
          }
        }
      }

      // Handle special cases for dropping on groups (if not targeting text products)
      if (
        !isTargetTextProductArea &&
        !overIdStr.startsWith("empty-placeholder-")
      ) {
        const targetItem = targetParent[targetIndex];
        // If dropping on a group header and the group is expanded, insert at the beginning of the group
        if (
          targetItem &&
          targetItem.isGroup &&
          !targetItem.isCollapsed &&
          targetItem.children
        ) {
          targetParent = targetItem.children;
          targetIndex = 0;
        }
      }

      // Apply restrictions before modifying the array structure

      // Restriction 1: Groups cannot be moved into Text products
      if (
        isTargetTextProductArea &&
        (sourceItem.isGroup || sourceItem.type === "GroupStart")
      ) {
        console.log("Cannot drop Group into Text's products");
        setActiveId(null);
        return;
      }

      // Restriction 2: Text items cannot be dropped into Text products or another Text item
      if (
        (isTargetTextProductArea || targetParent[targetIndex]?.isText) &&
        (sourceItem.isText || sourceItem.type === "Text")
      ) {
        console.log(
          "Cannot drop Text into Text's products or another Text item"
        );
        setActiveId(null);
        return;
      }

      // Restriction 3: Only items of allowed type can be dropped into Text products
      if (
        isTargetTextProductArea &&
        !ALLOWED_PRODUCT_TYPE_FOR_TEXT.includes(sourceItem.type as LineItemType)
      ) {
        console.log(
          `Only ${ALLOWED_PRODUCT_TYPE_FOR_TEXT.join(" or ")} items can be dropped into Text's products`
        );
        setActiveId(null);
        return;
      }

      // Restriction 4: Groups cannot be nested (existing rule)
      if (sourceItem.isGroup && targetParent !== newItems) {
        const isTargetInGroup = targetResult.path.length > 1;
        if (isTargetInGroup) {
          console.log("Cannot nest Groups");
          setActiveId(null);
          return;
        }
      }

      // Remove the item from its current position
      const [draggedItem] = sourceParent.splice(sourceIndex, 1);

      // If dropping into Text's products area
      if (isTargetTextProductArea && targetTextItem) {
        // Update the item to be a product of the Text item
        draggedItem.parentTextTrackingId = targetTextItem.trackingId;
        draggedItem.isGroup = false;
        draggedItem.isText = false;

        // Add to the Text item's children at the appropriate index
        targetParent.splice(targetIndex, 0, draggedItem);
      }
      // If moving an item out of Text's products
      else if (
        draggedItem.type === "Product" &&
        draggedItem.parentTextTrackingId
      ) {
        // Clear product properties when moving out of Text
        draggedItem.parentTextTrackingId = null;

        // Add to the target parent at the target index
        targetParent.splice(targetIndex, 0, draggedItem);
      }
      // Normal move (root or group)
      else {
        // Add the item to the target position
        targetParent.splice(targetIndex, 0, draggedItem);
      }

      // Update state and notify parent
      setHierarchicalItems(newItems);
      notifyChanges(newItems);
    } catch (error) {
      console.error("Error during drag operation:", error);
    }

    setActiveId(null);
  };

  // Toggle group collapse state
  const toggleGroupCollapse = (trackingId: string) => {
    setHierarchicalItems((prevItems) => {
      const newItems = JSON.parse(
        JSON.stringify(prevItems)
      ) as GroupedLineItem[];

      const toggleGroup = (items: GroupedLineItem[]): boolean => {
        for (let i = 0; i < items.length; i++) {
          if (items[i].trackingId === trackingId) {
            // Toggle collapsed state
            items[i].isCollapsed = !items[i].isCollapsed;

            // Update collapsed groups state
            setCollapsedGroups((prev) => {
              const newState = { ...prev };
              newState[trackingId] = items[i].isCollapsed!;

              // Save to localStorage
              try {
                const storageKey = `lineItemGroups-${Object.keys(newState).join(
                  "-"
                )}-collapsed`;
                localStorage.setItem(storageKey, JSON.stringify(newState));
              } catch (error) {
                console.error("Failed to save collapsed state:", error);
              }

              return newState;
            });

            return true;
          }

          if (
            items[i].isGroup &&
            items[i].children &&
            Array.isArray(items[i].children)
          ) {
            if (toggleGroup(items[i].children as GroupedLineItem[])) {
              return true;
            }
          }
        }

        return false;
      };

      toggleGroup(newItems);
      return newItems;
    });
  };

  // Add debugging function to check for tracking ID collisions
  const checkTrackingIdCollisions = (items: GroupedLineItem[], path = ''): void => {
    const seenIds = new Set<string>();
    
    const checkItems = (itemList: GroupedLineItem[], currentPath: string) => {
      itemList.forEach((item, index) => {
        const fullPath = `${currentPath}[${index}]`;
        
        if (seenIds.has(item.trackingId)) {
          console.error(`TRACKING ID COLLISION! ${item.trackingId} found multiple times. Current path: ${fullPath}`);
        } else {
          seenIds.add(item.trackingId);
        }
        
        console.log(`${fullPath}: ${item.trackingId} (${item.type}) - isText: ${item.isText}, hasChildren: ${!!item.children?.length}`);
        
        if (item.children && Array.isArray(item.children)) {
          checkItems(item.children, `${fullPath}.children`);
        }
      });
    };
    
    checkItems(items, path);
  };

  // Update a line item's properties
  const handleLineItemChange = (trackingId: string, newItem: LineItem) => {
    console.log('=== handleLineItemChange START ===');
    console.log('Target trackingId:', trackingId);
    console.log('New item data:', { type: newItem.type, title: newItem.title, parentTextTrackingId: newItem.parentTextTrackingId });
    
    // Check for collisions before update
    console.log('Checking for tracking ID collisions BEFORE update:');
    checkTrackingIdCollisions(hierarchicalItems, 'root');
    
    setHierarchicalItems((prevItems) => {
      const newItems = JSON.parse(
        JSON.stringify(prevItems)
      ) as GroupedLineItem[];

      const updateItem = (items: GroupedLineItem[], depth = 0): boolean => {
        const indent = '  '.repeat(depth);
        
        for (let i = 0; i < items.length; i++) {
          if (items[i].trackingId === trackingId) {
            console.log(`${indent}FOUND MATCH at depth ${depth}!`);
            console.log(`${indent}Current item:`, {
              type: items[i].type,
              title: items[i].title,
              trackingId: items[i].trackingId,
              isText: items[i].isText,
              isGroup: items[i].isGroup,
              hasChildren: !!items[i].children?.length,
              parentTextTrackingId: items[i].parentTextTrackingId
            });
            
            // Preserve special properties
            const wasCollapsed = items[i].isCollapsed;
            const isGroup = items[i].isGroup;
            const isText = items[i].isText;
            const children = items[i].children;
            const originalTrackingId = items[i].trackingId;
            const originalParentTextTrackingId = items[i].parentTextTrackingId;

            // Update with new values
            Object.assign(items[i], newItem);

            // Restore special properties and ensure tracking ID doesn't change
            items[i].trackingId = originalTrackingId;
            
            if (isGroup) {
              items[i].isGroup = isGroup;
              items[i].children = children;
              items[i].isCollapsed = wasCollapsed;
            } else if (isText) {
              items[i].isText = isText;
              items[i].children = children;
              items[i].isCollapsed = wasCollapsed;
            } else {
              // For regular items, preserve parentTextTrackingId if it existed
              if (originalParentTextTrackingId) {
                items[i].parentTextTrackingId = originalParentTextTrackingId;
              }
            }

            console.log(`${indent}After update:`, {
              type: items[i].type,
              title: items[i].title,
              trackingId: items[i].trackingId,
              isText: items[i].isText,
              isGroup: items[i].isGroup,
              hasChildren: !!items[i].children?.length,
              parentTextTrackingId: items[i].parentTextTrackingId
            });

            return true;
          }

          // Search through group children
          if (
            items[i].isGroup &&
            items[i].children &&
            Array.isArray(items[i].children)
          ) {
            if (updateItem(items[i].children as GroupedLineItem[], depth + 1)) {
              return true;
            }
          }

          // Also search through text item children
          if (
            items[i].isText &&
            items[i].children &&
            Array.isArray(items[i].children)
          ) {
            if (updateItem(items[i].children as GroupedLineItem[], depth + 1)) {
              return true;
            }
          }
        }

        return false;
      };

      const found = updateItem(newItems);
      console.log('Update result:', found);
      
      if (found) {
        console.log('Checking for tracking ID collisions AFTER update:');
        checkTrackingIdCollisions(newItems, 'root');
        notifyChanges(newItems);
      }
      
      console.log('=== handleLineItemChange END ===');
      return newItems;
    });
  };

  // Update a group's name
  const handleGroupNameChange = (trackingId: string, name: string) => {
    setHierarchicalItems((prevItems) => {
      const newItems = JSON.parse(
        JSON.stringify(prevItems)
      ) as GroupedLineItem[];

      const updateGroupName = (items: GroupedLineItem[]): boolean => {
        for (let i = 0; i < items.length; i++) {
          if (items[i].trackingId === trackingId) {
            items[i].groupName = name;
            return true;
          }

          if (
            items[i].isGroup &&
            items[i].children &&
            Array.isArray(items[i].children)
          ) {
            if (updateGroupName(items[i].children as GroupedLineItem[])) {
              return true;
            }
          }
        }

        return false;
      };

      updateGroupName(newItems);
      notifyChanges(newItems);
      return newItems;
    });
  };

  // Handle remove
  const handleRemove = async (trackingId: string, isPhoto: boolean = false) => {
    setIsLoadingRemove((prev) => ({ ...prev, [trackingId]: true }));
    if (isPhoto) {
      const photoId = parseInt(trackingId.split("-")[1]);
      const photo = photos.find((photo) => photo.photoid === photoId);
      if (!photo) {
        throw new Error("Photo not found");
      }
      await photoService.deletePhoto(photoId);

      await s3Service.deleteObject(
        `${companyName}/job/${jobId}/${photo.photoname_s3}.${photo.photoext}`
      );
      setPhotos((prevPhotos) =>
        prevPhotos.filter((photo) => photo.photoid !== photoId)
      );
      setIsLoadingRemove((prev) => ({ ...prev, [trackingId]: false }));
      return;
    }

    const itemToRemove = findItemByTrackingId(hierarchicalItems, trackingId);
    // For files and photos, delete from S3 first
    if (itemToRemove?.type === "File") {
      try {
        if (!itemToRemove.filenameS3 || !companyName || !jobId) {
          throw new Error("Missing required file information");
        }

        const key = itemToRemove.filenameS3;
        await s3Service.deleteObject(
          `${companyName}/job/${jobId}${
            itemToRemove.type === "File" ? "/files" : ""
          }/${key}`
        );
        removeItem(trackingId);
      } catch (error) {
        setIsLoadingRemove((prev) => ({ ...prev, [trackingId]: false }));
        console.error("Failed to delete file from S3:", error);
        // Continue with line item removal even if S3 deletion fails
        removeItem(trackingId);
      }
    } else {
      setIsLoadingRemove((prev) => ({ ...prev, [trackingId]: false }));
      removeItem(trackingId);
    }
  };

  // Actually perform the removal
  const removeItem = (trackingId: string) => {
    setHierarchicalItems((prevItems) => {
      const newItems = JSON.parse(
        JSON.stringify(prevItems)
      ) as GroupedLineItem[];

      const removeItemRecursive = (items: GroupedLineItem[]): boolean => {
        for (let i = 0; i < items.length; i++) {
          if (items[i].trackingId === trackingId) {
            // Remove the item
            items.splice(i, 1);
            return true;
          }

          if (
            items[i].isGroup &&
            items[i].children &&
            Array.isArray(items[i].children)
          ) {
            if (removeItemRecursive(items[i].children as GroupedLineItem[])) {
              return true;
            }
          }
        }

        return false;
      };

      removeItemRecursive(newItems);
      notifyChanges(newItems);
      return newItems;
    });
  };

  // Add a new line item
  const handleAddItem = (type: LineItemType) => {
    const newItem: LineItem = {
      trackingId: crypto.randomUUID(),
      type,
      title: null,
      description: null,
      quantity: type === "Text" ? 1 : type === "Product" ? 1 : null,
      unit: type === "Text" ? "HR" : type === "Product" ? "EA" : null,
      unitCost: null,
      cost: null,
      markup: null,
      applyTax: false,
      excludeAdditionalMarkup: false,
      materialTypeId: null,
      materialStyleId: null,
      materialSupplierId: null,
      laborCategoryId: null,
      laborTypeId: null,
      typeId: null,
      useQuoteFile: false,
      useClaimFile: false,
      useWorkOrderFile: false,
      date: null,
      accepted: false,
      initials: null,
      reason: null,
      reasonNotes: null,
      order: null,
      materialStyle: null,
      groupName: null,
      isCollapsed: false,
      sectionId: sectionId,
      appliedFormulaId: null,
      parentTextTrackingId: null,
    };

    setHierarchicalItems((prevItems) => {
      const newItems = [...prevItems, newItem];
      notifyChanges(newItems);
      return newItems;
    });

    setIsLineItemOpen(false);
  };

  // Add a new group
  const handleAddGroup = () => {
    const groupId = crypto.randomUUID();

    const newGroup: GroupedLineItem = {
      trackingId: groupId,
      type: "GroupStart",
      title: null,
      description: null,
      quantity: null,
      unit: null,
      unitCost: null,
      cost: null,
      markup: null,
      applyTax: false,
      excludeAdditionalMarkup: false,
      materialTypeId: null,
      materialStyleId: null,
      materialSupplierId: null,
      laborCategoryId: null,
      laborTypeId: null,
      typeId: null,
      useQuoteFile: false,
      useClaimFile: false,
      useWorkOrderFile: false,
      date: null,
      accepted: false,
      initials: null,
      reason: null,
      reasonNotes: null,
      order: null,
      materialStyle: null,
      groupName: "New Group",
      isGroup: true,
      children: [],
      isCollapsed: false,
      sectionId: sectionId,
      appliedFormulaId: null,
    };

    setHierarchicalItems((prevItems) => {
      const newItems = [...prevItems, newGroup];
      notifyChanges(newItems);
      return newItems;
    });

    setIsLineItemOpen(false);
  };

  const handleImportTemplate = async (templateId: number) => {
    setImportingTemplateId(templateId);
    try {
      const template = await quoteTemplateService.getQuoteTemplateById(
        templateId
      );
      const newLineItems = flatToHierarchical(
        template.lineItems.map((item) => {
          const formLineItem = templateLineItemFromApiModel(item);
          return {
            ...formLineItem,
            id: undefined,
            order: items.length + (item.ntiorder ?? 0),
            sectionId: sectionId,
            trackingId: crypto.randomUUID(),
          };
        })
      );

      setHierarchicalItems((prevItems) => {
        const newItems = [...prevItems, ...newLineItems];
        notifyChanges(newItems);
        return newItems;
      });
      setIsLineItemOpen(false);
    } catch (error) {
      console.error("Failed to import template:", error);
    } finally {
      setImportingTemplateId(null);
    }
  };

  // Initialize S3Service
  const s3Service = React.useMemo(
    () => new S3Service(apiUrl, apiStage),
    [apiUrl, apiStage]
  );

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const photoInputRef = React.useRef<HTMLInputElement>(null);
  const [isFileUploading, setIsFileUploading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);

  // Update file upload handler
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files || !event.target.files.length || !jobId) return;

    setIsLineItemOpen(false);
    setIsFileUploading(true);
    setUploadProgress(0);
    try {
      const response = await s3Service.uploadFiles(
        `${companyName}/job/${jobId}/files`,
        Array.from(event.target.files).map((file) => ({
          file,
          key: file.name,
        })),
        (progress) => {
          setUploadProgress(progress);
        }
      );

      // Create new line items for each uploaded file
      const newLineItems: LineItem[] = response.files
        .filter((file) => !file.isThumbnail)
        .map((uploadedFile) => ({
          trackingId: crypto.randomUUID(),
          type: "File" as LineItemType,
          title: uploadedFile.fileName,
          description: null,
          quantity: null,
          unit: null,
          unitCost: null,
          cost: null,
          markup: null,
          applyTax: false,
          excludeAdditionalMarkup: false,
          materialTypeId: null,
          materialStyleId: null,
          materialSupplierId: null,
          laborCategoryId: null,
          laborTypeId: null,
          typeId: null,
          useQuoteFile: false,
          useClaimFile: false,
          useWorkOrderFile: false,
          date: new Date().toISOString(),
          accepted: false,
          initials: null,
          reason: null,
          reasonNotes: null,
          order: null,
          materialStyle: null,
          groupName: null,
          isCollapsed: false,
          sectionId: sectionId,
          filename: uploadedFile.fileName,
          filenameS3: uploadedFile.key,
          appliedFormulaId: null,
        }));

      setHierarchicalItems((prevItems) => {
        const newItems = [...prevItems, ...newLineItems];
        notifyChanges(newItems);
        return newItems;
      });
    } catch (error) {
      console.error("Failed to upload file:", error);
    } finally {
      setIsFileUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setIsLineItemOpen(false);
    }
  };

  // Update photo upload handler
  const handlePhotoUpload = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files || !event.target.files.length || !jobId) return;

      setIsLineItemOpen(false);
      setIsFileUploading(true);
      setUploadProgress(0);
      try {
        const response = await s3Service.uploadFiles(
          `${companyName}/job/${jobId}`,
          Array.from(event.target.files).map((file) => ({
            file,
            key: file.name,
            isImage: true,
          })),
          (progress) => {
            setUploadProgress(progress);
          }
        );

        const raesUser = JSON.parse(
          localStorage.getItem("raesUser") || "{}"
        ) as { ufirstn: string; ulastn: string };
        const userFullName = `${raesUser.ufirstn} ${raesUser.ulastn}`;

        // Create new line items for each uploaded photo
        const newPhotos: Omit<Photo, "photoid">[] = response.files
          .filter((file) => !file.isThumbnail)
          .map((uploadedFile, index) => {
            const fileName = uploadedFile.fileName;
            const fileExtension = fileName.split(".").pop() || null;
            const fileNameWithoutExtension = fileName
              .replace(new RegExp(`${fileExtension}$`), "")
              .slice(0, -1);
            const keyWithoutExtension = uploadedFile.key
              .replace(new RegExp(`${fileExtension}$`), "")
              .slice(0, -1);

            return {
              pjobid: jobId,
              pwoid: workOrderId,
              pcid: null,
              pcinid: null,
              psectiontrackingid: sectionTrackingId,
              pwotype: workOrderType,
              puser: userFullName,
              photoname: fileNameWithoutExtension,
              photoname_s3: keyWithoutExtension,
              photoext: fileExtension,
              photocat: null,
              photodts: new Date(),
              photolabel: null,
              phototags: null,
              photoorder: photos.length + index + 1,
              photoidcc: null,
              photodtscc: null,
              photoreq: null,
              photonotes: null,
            };
          });

        const createPhotosResponse = await photoService.createPhotos(newPhotos);
        setPhotos([...photos, ...createPhotosResponse]);
      } catch (error) {
        console.error("Failed to upload photo:", error);
      } finally {
        setIsFileUploading(false);
        setUploadProgress(0);
        if (photoInputRef.current) {
          photoInputRef.current.value = "";
        }
        setIsLineItemOpen(false);
      }
    },
    [
      photoService,
      sectionTrackingId,
      jobId,
      workOrderType,
      companyName,
      setIsLineItemOpen,
      setIsFileUploading,
      setUploadProgress,
      photoInputRef,
      s3Service,
      photos,
      workOrderId,
    ]
  );

  // Handle changes to Text item's products
  const handleTextItemProductsChange = (
    textItemTrackingId: string,
    newChildren: GroupedLineItem[]
  ) => {
    setHierarchicalItems((prevItems) => {
      const newItems = JSON.parse(
        JSON.stringify(prevItems)
      ) as GroupedLineItem[]; // Deep clone

      const updateTextChildrenRecursive = (
        items: GroupedLineItem[]
      ): boolean => {
        for (let i = 0; i < items.length; i++) {
          if (items[i].trackingId === textItemTrackingId && items[i].isText) {
            // Instead of replacing all children, merge the changes more carefully
            const existingChildren = items[i].children || [];
            const updatedChildren: GroupedLineItem[] = [];
            
            // First, add all the children from newChildren (these are the updated ones)
            for (const newChild of newChildren) {
              updatedChildren.push({
                ...newChild,
                // Ensure children have correct parentTextTrackingId flag
                parentTextTrackingId: textItemTrackingId,
                // Keep the original type (Product or Labor)
                type: newChild.type,
              });
            }
            
            // Then, add any existing children that aren't in newChildren
            // This handles the case where an item was dragged in but isn't in the newChildren array
            for (const existingChild of existingChildren) {
              const isAlreadyIncluded = updatedChildren.some(
                (newChild) => newChild.trackingId === existingChild.trackingId
              );
              
              if (!isAlreadyIncluded && existingChild.parentTextTrackingId === textItemTrackingId) {
                updatedChildren.push({
                  ...existingChild,
                  parentTextTrackingId: textItemTrackingId,
                });
              }
            }
            
            items[i].children = updatedChildren;
            return true;
          }
          // Check if children exists, is an array, and is not empty
          const children = items[i].children;
          if (children && Array.isArray(children) && children.length > 0) {
            // Use non-null assertion since we've verified children exists and is an array
            const found = updateTextChildrenRecursive(children);
            if (found) return true;
          }
        }
        return false;
      };

      updateTextChildrenRecursive(newItems);
      notifyChanges(newItems);
      return newItems;
    });
  };

  // Render a single item (regular or group)
  const renderItem = (item: GroupedLineItem, isPreview: boolean = false) => {
    if (item.isGroup) {
      return (
        <motion.div
          initial={!isPreview ? { opacity: 0 } : undefined}
          animate={!isPreview ? { opacity: 1 } : undefined}
          exit={!isPreview ? { opacity: 0 } : undefined}
          transition={!isPreview ? { duration: 0.2 } : undefined}
          style={isPreview ? { pointerEvents: "none" } : undefined}
        >
          <SortableGroupHeader
            item={item}
            toggleGroupCollapse={toggleGroupCollapse}
            handleGroupNameChange={handleGroupNameChange}
            handleRemove={handleRemove}
            isPreview={isPreview}
            groupIndex={-1}
          >
            {!item.isCollapsed && item.children && (
              <div className="pl-4 ml-6 overflow-hidden border-l-2 border-blue-200">
                <AnimatePresence>
                  {item.children.map((childItem) => (
                    <React.Fragment key={childItem.trackingId}>
                      {renderItem(childItem, isPreview)}
                    </React.Fragment>
                  ))}
                </AnimatePresence>
                {item.children.length === 0 && (
                  <EmptyGroupPlaceholder groupId={item.trackingId} />
                )}
              </div>
            )}
          </SortableGroupHeader>
        </motion.div>
      );
    }

    return (
      <motion.div
        initial={!isPreview ? { opacity: 0 } : undefined}
        animate={!isPreview ? { opacity: 1 } : undefined}
        exit={!isPreview ? { opacity: 0 } : undefined}
        transition={!isPreview ? { duration: 0.2 } : undefined}
        style={isPreview ? { pointerEvents: "none" } : undefined}
      >
        <LineItemForm
          key={`${item.trackingId}-${item.children?.length || 0}`}
          value={item}
          productStyleOptions={localProductStyleOptions}
          onChange={(updatedItem) =>
            handleLineItemChange(item.trackingId, updatedItem)
          }
          onChildrenChange={
            item.isText
              ? (newChildren) =>
                  handleTextItemProductsChange(item.trackingId, newChildren)
              : undefined
          }
          onRemove={() => handleRemove(item.trackingId)}
          id={isPreview ? undefined : item.trackingId}
          activeId={activeId ? activeId.toString() : null}
          apiStage={apiStage}
          isTemplate={isTemplate}
          warranties={warranties}
          materialFormulas={materialFormulas}
          quoteCalculator={quoteCalculator}
          crewRates={crewRates}
          laborTypeOptions={laborTypeOptions} // Use test labor type options
        />
      </motion.div>
    );
  };

  // Get the active item for the drag overlay
  const getActiveItem = () => {
    if (!activeId) return null;

    const activeItem = findItemByTrackingId(
      hierarchicalItems,
      activeId.toString()
    );
    if (!activeItem) return null;

    return renderItem(activeItem, true);
  };

  // Handle adding selected photos from job
  const handleAddSelectedPhotos = (selectedPhotos: Photo[]) => {
    // Just add photos to the current section
    setPhotos((prevPhotos) => [...prevPhotos, ...selectedPhotos]);
  };

  return (
      <div className="space-y-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={itemTrackingIds}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4">
            <AnimatePresence>
              {hierarchicalItems
                .filter((item) => item.type !== "File" && item.type !== "Photo")
                .map((item) => (
                  <React.Fragment key={item.trackingId}>
                    {renderItem(item, false)}
                  </React.Fragment>
                ))}
            </AnimatePresence>
          </div>
        </SortableContext>

        <DragOverlay dropAnimation={null}>
          <div className="w-full">{getActiveItem()}</div>
        </DragOverlay>
      </DndContext>

      {/* Add PhotoPickerModal */}
      {jobId && sectionTrackingId && (
        <PhotoPickerModal
          open={isPhotoPickerOpen}
          onOpenChange={setIsPhotoPickerOpen}
          jobId={jobId}
          sectionTrackingId={sectionTrackingId}
          companyName={companyName}
          photoService={photoService}
          s3Service={s3Service}
          onPhotosSelected={handleAddSelectedPhotos}
          excludePhotos={photos.map((photo) => photo.photoid)}
          sectionName={sectionName}
          refreshSection={refreshSection}
        />
      )}

      {/* Add buttons */}
      <div className="flex items-center justify-start gap-2">
        <Input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple={true}
          onChange={handleFileUpload}
          disabled={isFileUploading}
        />
        <Input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          multiple={true}
          className="hidden"
          onChange={handlePhotoUpload}
          disabled={isFileUploading}
        />
        <Popover open={isLineItemOpen} onOpenChange={setIsLineItemOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline-primary" size="sm">
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Item(s)
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search items..." />
              <CommandList>
                <CommandEmpty>No types found.</CommandEmpty>
                <CommandGroup heading="Line Items">
                  {UI_LINE_ITEM_TYPES.filter(
                    (type) => type.value !== "File" && type.value !== "Photo"
                  ).map((type) => {
                    const Icon = type.icon;
                    return (
                      <CommandItem
                        key={type.value}
                        onSelect={() => {
                          if (type.value === "Group") {
                            handleAddGroup();
                          } else {
                            handleAddItem(type.value as LineItemType);
                          }
                        }}
                        className="flex items-center gap-2"
                      >
                        <Icon className={cn("w-4 h-4", `text-${type.color}`)} />
                        <span>{type.label}</span>
                      </CommandItem>
                    );
                  })}
                  <Popover
                    open={isTemplateMenuOpen}
                    onOpenChange={setIsTemplateMenuOpen}
                  >
                    <PopoverTrigger asChild>
                      <div
                        role="button"
                        onMouseEnter={() => setIsTemplateMenuOpen(true)}
                        onMouseLeave={() => setIsTemplateMenuOpen(false)}
                        className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                      >
                        <div className="flex items-center gap-2">
                          <Import className="w-4 h-4" />
                          <span>Import From Template...</span>
                        </div>
                        <ChevronRightIcon className="w-4 h-4 ml-auto" />
                      </div>
                    </PopoverTrigger>
                    <PopoverContent
                      side="right"
                      align="start"
                      className="w-[300px] p-0"
                      sideOffset={-4}
                      onMouseEnter={() => setIsTemplateMenuOpen(true)}
                      onMouseLeave={() => setIsTemplateMenuOpen(false)}
                    >
                      <Command>
                        <CommandInput placeholder="Search templates..." />
                        <CommandList>
                          <CommandEmpty>No templates found.</CommandEmpty>
                          <CommandGroup>
                            {templates.map((template) => (
                              <CommandItem
                                key={template.ntid}
                                onSelect={() =>
                                  handleImportTemplate(template.ntid)
                                }
                                className="flex items-center justify-between"
                              >
                                <span>{template.nttitle}</span>
                                {importingTemplateId === template.ntid && (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </CommandGroup>
                {jobId && (
                  <CommandGroup heading="Attachments">
                    <CommandItem
                      onSelect={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2"
                      disabled={isFileUploading}
                    >
                      <FileIcon className="w-4 h-4 text-blue-500" />
                      <span>Upload File</span>
                    </CommandItem>
                    <CommandItem
                      onSelect={() => photoInputRef.current?.click()}
                      className="flex items-center gap-2"
                      disabled={isFileUploading}
                    >
                      <ImageIcon className="w-4 h-4 text-green-500" />
                      <span>Upload Photo</span>
                    </CommandItem>
                    {/* Add new option for selecting photos from job */}
                    <CommandItem
                      onSelect={() => setIsPhotoPickerOpen(true)}
                      className="flex items-center gap-2"
                      disabled={isFileUploading || !sectionTrackingId}
                    >
                      <Images className="w-4 h-4 text-green-500" />
                      <span>Select Photo(s) from Job</span>
                    </CommandItem>
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {isFileUploading && (
          <div className="flex items-center gap-2 ml-2">
            <Progress value={uploadProgress} className="w-24 h-2" />
            <span className="text-xs text-muted-foreground">
              {Math.round(uploadProgress)}%
            </span>
          </div>
        )}
      </div>

      {/* File Explorer */}
      <FileExplorer
        items={items}
        onRemove={handleRemove}
        isLoadingRemove={isLoadingRemove}
        s3Service={s3Service}
        companyName={companyName}
        jobId={jobId}
        photos={photos}
        sectionId={sectionId}
        photoService={photoService}
        fetchPhotos={fetchPhotos}
      />
    </div>
  );
}
